import React from 'react';
import ReactECharts from 'echarts-for-react';
import {
  getPartnerNarrativeColor,
  buildChartTheme,
  truncateChartLabel,
} from './chartTheme';
import useLaptopCompact from './useLaptopCompact';
import ChartEmptyState from './ChartEmptyState';

const partnerSplitLine = {
  lineStyle: { type: 'dashed', color: 'rgba(148, 163, 184, 0.32)', width: 1 },
};

const PartnerHorizontalBarChart = ({
  data,
  renderZeroWhenEmpty = false,
  emptyStateTitle = 'No activity for this period',
  emptyStateSubtitle = 'Try changing the time filter.',
  emptyStateCompact = false,
}) => {
  const isLaptopCompact = useLaptopCompact();
  const theme = buildChartTheme(isLaptopCompact);

  const hasData = Array.isArray(data) && data.length > 0;
  if (!hasData && !renderZeroWhenEmpty) {
    return (
      <ChartEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        compact={emptyStateCompact}
      />
    );
  }
  const normalizedData = hasData ? data : [{ name: '', count: 0 }];
  const sorted = [...normalizedData].sort(
    (a, b) => (b.count || b.Count || 0) - (a.count || a.Count || 0)
  );
  const names = sorted.map((d) => d.name);
  const values = sorted.map((d) => d.count || d.Count || 0);

  const options = {
    animation: false,
    grid: {
      ...theme.gridHorizontalBar,
      left: isLaptopCompact ? 10 : 15,
      right: isLaptopCompact ? 34 : 36,
      top: 16,
      bottom: 16,
      containLabel: true,
    },
    tooltip: {
      ...theme.tooltipBase,
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => `${params[0].name}: ${params[0].value}`,
    },
    xAxis: {
      type: 'value',
      min: 0,
      axisLabel: theme.axisLabel,
      splitLine: partnerSplitLine,
    },
    yAxis: {
      type: 'category',
      data: names,
      axisLabel: {
        ...theme.axisLabel,
        width: isLaptopCompact ? 155 : 160,
        margin: 10,
        overflow: 'truncate',
        formatter: (value) => truncateChartLabel(value, 22),
      },
    },
    series: [{
      type: 'bar',
      barMaxWidth: 24,
      data: values.map((v, i) => ({
        value: v,
        itemStyle: { color: getPartnerNarrativeColor(names[i], i) },
      })),
      label: { show: true, position: 'right', fontSize: theme.fs(12), color: '#374151' },
    }],
  };

  const rowHeight = isLaptopCompact ? 30 : 32;

  return (
    <ReactECharts
      option={options}
      style={{ height: `${Math.max(280, names.length * rowHeight + (isLaptopCompact ? 54 : 60))}px`, width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default PartnerHorizontalBarChart;
